import type { MascotPose } from '@shared';

/**
 * What a mascot does in each situation — data, not code, so a new moment is a new
 * row here and (if it wants new art) a new pose in `@shared/mascot`.
 *
 * - `pose` is the image to ask for; the shared fallback chain covers a realm that
 *   has not drawn it, so `jackpot` quietly becomes `celebrate` becomes `idle`.
 * - `motion` names a keyframe animation in `Mascot.module.css`. Every one of them
 *   moves only `transform` and `opacity`, like the wheel.
 * - `blink` lets a realm with a `blink` image have the mascot close its eyes now
 *   and then; without that image it is simply ignored.
 * - `sparkle` adds the little stars round the mascot.
 */
export interface MascotStateDefinition {
  pose: MascotPose;
  motion: 'bob' | 'jitter' | 'hop' | 'hopBig' | 'droop';
  blink: boolean;
  sparkle: boolean;
}

export type MascotState = 'idle' | 'spinning' | 'celebrate' | 'jackpot' | 'sad';

export const MASCOT_STATES: Record<MascotState, MascotStateDefinition> = {
  idle: { pose: 'idle', motion: 'bob', blink: true, sparkle: false },
  spinning: { pose: 'wave', motion: 'jitter', blink: false, sparkle: false },
  celebrate: { pose: 'celebrate', motion: 'hop', blink: false, sparkle: true },
  jackpot: { pose: 'jackpot', motion: 'hopBig', blink: false, sparkle: true },
  sad: { pose: 'sad', motion: 'droop', blink: false, sparkle: false },
};

/** Which face the placeholder wears for a state — it has no art to switch between. */
export type PlaceholderFace = 'open' | 'wave' | 'happy' | 'stars' | 'sad';

export const PLACEHOLDER_FACES: Record<MascotState, PlaceholderFace> = {
  idle: 'open',
  spinning: 'wave',
  celebrate: 'happy',
  jackpot: 'stars',
  sad: 'sad',
};

/**
 * How the mascot reacts to a spin's result. The tier comes from the server's draw,
 * already reduced from the real odds, so this reveals nothing the reward dialog
 * does not.
 */
export function mascotStateForOutcome(
  reward: string,
  tier: 'none' | 'win' | 'jackpot',
): MascotState {
  if (reward === 'nothing') return 'sad';
  if (tier === 'jackpot') return 'jackpot';
  return 'celebrate';
}
